import { engineToCanonical } from "../utils/board-geometry";

interface RowPickerSheetProps {
  readonly begin: string | null;
  readonly end: string | null;
  readonly onReset: () => void;
}

interface SlotProps {
  readonly name: "begin" | "end";
  readonly label: string;
  readonly value: string | null;
}

function Slot({ name, label, value }: SlotProps) {
  return (
    <div
      data-testid={`row-pick-slot-${name}`}
      data-filled={value !== null}
      style={{
        flex: 1,
        padding: "8px 12px",
        borderRadius: "var(--cn-radius-md)",
        border: `1px solid ${value === null ? "var(--cn-border-subtle)" : "var(--cn-yinsh-row-highlight)"}`,
        background: "var(--cn-surface-secondary)",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "var(--cn-text-muted)",
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--cn-font-mono, ui-monospace, monospace)",
          fontSize: 16,
          fontWeight: 700,
          color: "var(--cn-text-primary)",
        }}
      >
        {value === null ? "—" : engineToCanonical(value)}
      </div>
    </div>
  );
}

/**
 * Mandatory 5-row scoring picker. Scoring cannot be declined; when 5-rows
 * overlap the player disambiguates by tapping the run's two ends on the board.
 * Contract: docs/wireframes/yinsh/specs/active-game.md (phase 4).
 */
export function RowPickerSheet({ begin, end, onReset }: RowPickerSheetProps) {
  const hasPick = begin !== null || end !== null;
  return (
    <div
      data-testid="row-picker-sheet"
      style={{
        background: "var(--cn-surface-primary)",
        border: "1px solid var(--cn-yinsh-row-highlight)",
        borderRadius: "var(--cn-radius-lg)",
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 13,
          lineHeight: 1.5,
          color: "var(--cn-text-secondary)",
        }}
      >
        5목 점수화는 필수입니다. 가져갈 5목의 시작점과 끝점을 보드에서
        선택하세요.
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        <Slot name="begin" label="시작점" value={begin} />
        <Slot name="end" label="끝점" value={end} />
      </div>
      <button
        type="button"
        data-testid="row-pick-reset"
        onClick={onReset}
        disabled={!hasPick}
        style={{
          alignSelf: "flex-end",
          minHeight: 44,
          padding: "8px 16px",
          background: "transparent",
          color: hasPick ? "var(--cn-text-primary)" : "var(--cn-text-muted)",
          border: "1px solid var(--cn-border-default)",
          borderRadius: 999,
          fontWeight: 600,
          fontSize: 13,
          cursor: hasPick ? "pointer" : "default",
        }}
      >
        선택 초기화
      </button>
    </div>
  );
}
